import { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import api, { apiError } from '../../lib/api';
import { toast } from 'sonner';

const inputCls = 'w-full h-11 px-4 rounded-xl bg-bh-canvas border border-white/10 text-sm placeholder:text-gray-600 focus:outline-none focus:border-bh-accent/60 transition-colors';

const AuthModal = () => {
  const { authOpen, closeAuth, login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email: form.email });
        toast.success('E-posta kayıtlıysa sıfırlama bağlantısı gönderildi. Spam / Gereksiz klasörlerini kontrol etmeyi unutmayın.');
        setMode('login');
      } else if (mode === 'login') {
        const u = await login(form.email, form.password);
        toast.success(`Hoş geldin, ${u.username ? `@${u.username}` : u.name}!`);
        closeAuth();
      } else {
        await register({
          username: form.username,
          email: form.email,
          password: form.password,
        });
        toast.success('Kayıt tamamlandı! Profilini düzenleyebilirsin.');
        closeAuth();
      }
      setForm({ username: '', email: '', password: '' });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent className="bg-bh-surface border-white/10 text-bh-txt sm:max-w-md p-0 overflow-y-auto" data-testid="auth-modal">
        <div className="grid grid-cols-2 border-b border-white/10">
          {[['login', 'Giriş Yap'], ['register', 'Kayıt Ol']].map(([m, label]) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => { setMode(m); setError(''); }}
              className={`py-4 text-sm font-display font-bold transition-colors ${mode === m ? 'text-bh-accent bg-bh-accent/5 border-b-2 border-bh-accent' : 'text-gray-400 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="p-4 sm:p-6 space-y-4">
          <div className="text-center mb-2">
            <h2 className="font-display font-extrabold text-lg">
              {mode === 'forgot' ? 'Şifre sıfırla' : mode === 'login' ? 'Tekrar hoş geldin!' : 'Hemen kayıt ol'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'forgot'
                ? 'Kayıtlı e-postana bu siteden bir bağlantı gider. Spam / Gereksiz klasörlerini de kontrol et.'
                : mode === 'login'
                  ? 'Puanların seni bekliyor.'
                  : 'Üye ol, profilini kur. Çark ve görevler için sonra Telegram bağlarsın.'}
            </p>
          </div>
          {mode === 'register' && (
            <>
              <input
                data-testid="auth-username-input"
                className={inputCls}
                placeholder="Kullanıcı adı"
                value={form.username}
                onChange={set('username')}
                required
                minLength={3}
                maxLength={20}
                autoComplete="username"
              />
            </>
          )}
          <input data-testid="auth-email-input" className={inputCls} type="email" placeholder="E-posta" value={form.email} onChange={set('email')} required />
          {mode !== 'forgot' && (
            <input data-testid="auth-password-input" className={inputCls} type="password" placeholder={mode === 'register' ? 'Şifre (en az 8 karakter)' : 'Şifre'} value={form.password} onChange={set('password')} required minLength={mode === 'register' ? 8 : 6} />
          )}
          {mode === 'login' && (
            <button
              type="button"
              data-testid="auth-forgot-link"
              onClick={() => { setMode('forgot'); setError(''); }}
              className="text-xs text-gray-500 hover:text-bh-accent"
            >
              Şifremi unuttum
            </button>
          )}
          {error && <p data-testid="auth-error" className="text-xs text-bh-crimson bg-bh-crimson/10 border border-bh-crimson/30 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full btn-accent py-3 rounded-xl text-sm disabled:opacity-50">
            {busy ? 'Lütfen bekle...' : mode === 'forgot' ? 'Bağlantı gönder' : mode === 'login' ? 'Giriş Yap' : 'Kayıt Ol'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
